import type { ComponentMultiStyleConfig } from '@chakra-ui/theme';
import { tagAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';
import { Badge } from './Badge';

import { TextStyles, Radii, Colors, FontWeights, Space } from '../../types';
const { definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

export const Tag: ComponentMultiStyleConfig = {
  baseStyle: {
    container: {
      gap: 1.5,
      px: Space.XS,
      py: Space['2XS'],
      ring: '1px',
      ringInset: 'inset',
      ringColor: 'var(--badge-shadow)',
      minH: 'var(--tag-min-height)',
      display: 'inline-flex',
      flexGrow: '0',
      alignItems: 'center',
      alignSelf: 'center',
      '&': {
        apply: `textStyles.${TextStyles.UI_DEFAULT}`,
      },
      _disabled: {
        bg: Colors.SURFACE_DISABLED,
        color: Colors.TEXT_DISABLED,
        '--badge-iconColor': Colors.TEXT_DISABLED,
      },
    },
    label: {
      fontWeight: FontWeights.MEDIUM,
      display: 'inline-flex',
      gap: Space.XS,
    },
    closeButton: {
      ms: 0,
      opacity: '.72',
      boxSize: 'calc(var(--tag-min-height) - 0.5rem)',
      borderRadius: Radii.BASE,
      _hover: {
        opacity: '1',
        bg: 'color-mix(in srgb, currentColor 8%, transparent)',
        color: 'var(--badge-color)',
      },
      _active: {
        opacity: '1',
        bg: 'color-mix(in srgb, currentColor 12%, transparent)',
        color: 'var(--badge-color)',
      },
      '& > svg': {
        alignSelf: 'center',
        fontSize: '1em',
      },
    },
  },
  sizes: {
    sm: {
      container: {
        '--tag-min-height': `space.${Space.SPACE_6}`,
        gap: Space['2XS'],
        borderRadius: Radii.BASE,
        '&': {
          apply: `textStyles.${TextStyles.UI_COMPACT}`,
        },
      },
      closeButton: {
        my: -1,
        me: -2,
        borderRadius: 'inherit',
        boxSize: 'var(--tag-min-height)',
      },
    },
    md: {
      container: {
        '--tag-min-height': `space.${Space.SPACE_7}`,
        borderRadius: Radii.MD,
      },
    },
    lg: {
      container: {
        '--tag-min-height': `space.${Space.SPACE_8}`,
        '--tag-padding-inline': `space.${Space.SM}`,
        borderRadius: Radii.LG,
        h: 'var(--tag-min-height)',
        minH: 'var(--tag-min-height)',
        px: 'var(--tag-padding-inline)',
      },
      closeButton: {
        me: '-2',
      },
    },
  },
  variants: {
    subtle: definePartsStyle(props => ({
      container: {
        ...Badge.variants?.subtle(props),
      },
    })),
    solid: definePartsStyle(props => ({
      container: {
        ...Badge.variants?.solid(props),
      },
    })),
    outline: definePartsStyle(props => ({
      container: {
        ...Badge.variants?.outline(props),
      },
    })),
  },
  parts: [],
};
